import React, { useContext, useEffect, useState, useMemo } from "react";
import {
    Loader2,
    MessageCircle,
    Mail,
    Smartphone,
    Phone,
    Plug,
    CheckCircle2,
    Clock,
    XCircle,
    RefreshCw,
} from "lucide-react";
import { callComConfigApi } from "./callComConfigApi";
import AuthContext  from "../../../store/auth-context";
import ActiveStoreContext from "../../../store/ActiveStoreContext";
import styles from "./ComConfigSenderConnections.module.css";

// Note: comConfigRevokeSenderCredential needs a senderCredentialId, but
// the store-facing comConfigGetSenderProfiles response doesn't expose
// credential IDs (no comConfigGetSenderCredentials read endpoint exists
// for stores in the 17). So this screen offers "Replace credentials"
// (calls comConfigSaveSenderCredential, which archives whatever was
// ACTIVE server-side) rather than a standalone revoke action — flag if
// explicit single-credential revoke from this screen is actually needed,
// that's a small additive endpoint away.

const CHANNEL_ICONS = {
    WHATSAPP: MessageCircle,
    SMS: Smartphone,
    EMAIL: Mail,
    VOICE: Phone,
};

const STATUS_META = {
    ACTIVE: { label: "Active", icon: CheckCircle2, className: "statusActive" },
    PENDING_VERIFICATION: { label: "Pending verification", icon: Clock, className: "statusPending" },
    SUSPENDED: { label: "Suspended", icon: XCircle, className: "statusSuspended" },
    REVOKED: { label: "Revoked", icon: XCircle, className: "statusSuspended" },
};

const ComConfigSenderConnections = () => {
    const authCtx = useContext(AuthContext);
    const { activeStore: activeStoreJson } = useContext(ActiveStoreContext);
    const activeStore = (() => { try { return activeStoreJson ? JSON.parse(activeStoreJson) : null; } catch { return null; } })();

    const [profiles, setProfiles] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [connectingChannelId, setConnectingChannelId] = useState(null);
    const [displayName, setDisplayName] = useState("");
    const [credentialJson, setCredentialJson] = useState("");
    const [maskedPreview, setMaskedPreview] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [message, setMessage] = useState(null);

    useEffect(() => {
        loadProfiles();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStore?.storeIdentifier]);

    const loadProfiles = async () => {
        if (!activeStore?.storeIdentifier || !authCtx?.token) {
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        const res = await callComConfigApi(
            "comConfigGetSenderProfiles",
            { storeIdentifier: activeStore.storeIdentifier },
            authCtx
        );
        if (res?.status === "success") {
            setProfiles(Array.isArray(res.response) ? res.response : []);
        }
        setIsLoading(false);
    };

    const groupedByChannel = useMemo(() => {
        const groups = {};
        profiles.forEach((profile) => {
            if (!groups[profile.channelId]) {
                groups[profile.channelId] = {
                    channelId: profile.channelId,
                    channelCode: profile.channelCode,
                    channelName: profile.channelName,
                    platform: null,
                    own: null,
                };
            }
            if (profile.storeIdentifier === "PLATFORM") {
                groups[profile.channelId].platform = profile;
            } else {
                groups[profile.channelId].own = profile;
            }
        });
        return Object.values(groups);
    }, [profiles]);

    const openConnectForm = (channelId) => {
        setConnectingChannelId(channelId);
        setDisplayName("");
        setCredentialJson("");
        setMaskedPreview("");
        setMessage(null);
    };

    const handleConnect = async (channel) => {
        setIsSubmitting(true);
        setMessage(null);

        let createRes = channel.own;
        if (!channel.own) {
            createRes = await callComConfigApi(
                "comConfigSaveSenderProfile",
                {
                    storeIdentifier: activeStore?.storeIdentifier,
                    channelId: channel.channelId,
                    displayName,
                    authIdentifier: authCtx?.userId,
                },
                authCtx
            );
            if (createRes?.status !== "success") {
                setMessage({ type: "error", text: createRes?.message || "Couldn't connect. Try again." });
                setIsSubmitting(false);
                return;
            }
        }

        const senderProfileId = channel.own?.ID || createRes?.response?.senderProfileId;

        let parsedCredential = credentialJson;
        try {
            parsedCredential = JSON.parse(credentialJson);
        } catch (e) {
            // not JSON, send as raw string
        }

        const credRes = await callComConfigApi(
            "comConfigSaveSenderCredential",
            {
                senderProfileId,
                credentialPayload: parsedCredential,
                maskedPreview,
                authIdentifier: authCtx?.userId,
            },
            authCtx
        );

        if (credRes?.status === "success") {
            setMessage({ type: "success", text: "Connection saved." });
            setConnectingChannelId(null);
            loadProfiles();
        } else {
            setMessage({ type: "error", text: credRes?.message || "Saved the profile but credentials failed." });
        }

        setIsSubmitting(false);
    };

    if (isLoading) {
        return (
            <div className={styles.stateContainer}>
                <Loader2 className={styles.spinner} size={22} />
                <span>Loading connections…</span>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1 className={styles.title}>Sender connections</h1>
                <p className={styles.subtitle}>
                    By default, messages send through Lokaci's shared accounts. Connect your own WhatsApp Business
                    API, SMS sender ID, or email domain to send under your own name instead.
                </p>
            </div>

            <div className={styles.channelList}>
                {groupedByChannel.map((channel) => {
                    const ChannelIcon = CHANNEL_ICONS[channel.channelCode] || MessageCircle;
                    const ownStatus = channel.own ? STATUS_META[channel.own.status] : null;
                    const OwnStatusIcon = ownStatus?.icon;

                    return (
                        <div key={channel.channelId} className={styles.channelCard}>
                            <div className={styles.channelCardHeader}>
                                <ChannelIcon size={18} />
                                <span className={styles.channelName}>{channel.channelName}</span>
                            </div>

                            <div className={styles.channelRow}>
                                <span className={styles.channelRowLabel}>Platform default</span>
                                <span className={styles.statusActive}>
                                    <CheckCircle2 size={13} />
                                    Always available
                                </span>
                            </div>

                            <div className={styles.channelRow}>
                                <span className={styles.channelRowLabel}>Your own account</span>
                                {channel.own ? (
                                    <span className={styles[ownStatus.className]}>
                                        <OwnStatusIcon size={13} />
                                        {ownStatus.label}
                                    </span>
                                ) : (
                                    <span className={styles.notConnected}>Not connected</span>
                                )}
                            </div>

                            <div className={styles.channelActions}>
                                <button className={styles.connectButton} onClick={() => openConnectForm(channel.channelId)}>
                                    <Plug size={13} />
                                    {channel.own ? "Replace credentials" : "Connect your own"}
                                </button>
                            </div>

                            {connectingChannelId === channel.channelId && (
                                <div className={styles.connectForm}>
                                    {!channel.own && (
                                        <label className={styles.fieldLabel}>
                                            Label
                                            <input
                                                className={styles.fieldInput}
                                                type="text"
                                                value={displayName}
                                                onChange={(e) => setDisplayName(e.target.value)}
                                                placeholder="e.g. Black & Blush WhatsApp"
                                            />
                                        </label>
                                    )}
                                    <label className={styles.fieldLabel}>
                                        Credentials (paste what your provider gave you)
                                        <textarea
                                            className={styles.fieldTextarea}
                                            rows={4}
                                            value={credentialJson}
                                            onChange={(e) => setCredentialJson(e.target.value)}
                                        />
                                    </label>
                                    <label className={styles.fieldLabel}>
                                        Reference (last few characters, for display only)
                                        <input
                                            className={styles.fieldInput}
                                            type="text"
                                            value={maskedPreview}
                                            onChange={(e) => setMaskedPreview(e.target.value)}
                                            placeholder="••••1234"
                                        />
                                    </label>

                                    <div className={styles.formActions}>
                                        <button
                                            className={styles.cancelButton}
                                            onClick={() => setConnectingChannelId(null)}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            className={styles.saveButton}
                                            onClick={() => handleConnect(channel)}
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? (
                                                <Loader2 size={14} className={styles.spinnerInline} />
                                            ) : (
                                                <RefreshCw size={13} />
                                            )}
                                            Save connection
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {message && (
                <div className={message.type === "success" ? styles.messageSuccess : styles.messageError}>
                    {message.text}
                </div>
            )}
        </div>
    );
};

export default ComConfigSenderConnections;